import { useState, useMemo, useRef } from 'react';
import type { Hip3Market } from '../types';
import type { MarketStats } from '../services/marketData';
import { AssetLogo } from './AssetLogo';

interface MarketSelectorProps {
  markets: Hip3Market[];
  selectedMarket: Hip3Market | null;
  onSelect: (market: Hip3Market) => void;
  marketData?: Map<string, MarketStats>;
}

interface AggregatedMarketRow {
  baseAsset: string;
  markets: Hip3Market[];
  bestPrice: number | null;
  bestChange24h: number | null;
  avgFunding8h: number | null;
  totalVolume: number;
  totalOI: number;
  venueCount: number;
  maxLeverage: number;
  primaryMarket: Hip3Market;
}

export function MarketSelector({ markets, selectedMarket, onSelect, marketData }: MarketSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Group markets by base asset and aggregate data
  const aggregatedRows = useMemo(() => {
    const grouped = new Map<string, Hip3Market[]>();

    // Group by base asset
    markets.forEach(m => {
      if (!grouped.has(m.baseAsset)) {
        grouped.set(m.baseAsset, []);
      }
      grouped.get(m.baseAsset)!.push(m);
    });

    // Create aggregated rows
    const rows: AggregatedMarketRow[] = [];

    grouped.forEach((assetMarkets, baseAsset) => {
      let totalVolume = 0;
      let totalOI = 0;
      let bestPrice: number | null = null;
      let bestChange24h: number | null = null;
      let fundingValues: number[] = [];
      let maxLeverage = 0;

      assetMarkets.forEach(market => {
        // Track max leverage across all venues
        maxLeverage = Math.max(maxLeverage, market.maxLeverage);

        const stats = marketData?.get(market.id);
        if (stats) {
          totalVolume += stats.volume24h;
          totalOI += stats.openInterest;

          // Use first price we find (they should be similar across venues)
          if (bestPrice === null) {
            bestPrice = stats.lastPrice;
            bestChange24h = stats.change24h;
          }

          fundingValues.push(stats.funding8h);
        }
      });

      // Average funding across venues
      const avgFunding8h = fundingValues.length > 0
        ? fundingValues.reduce((a, b) => a + b, 0) / fundingValues.length
        : null;

      // Pick primary market: prefer native, fallback to first market
      const primaryMarket = assetMarkets.find(m => m.isNative) || assetMarkets[0];

      rows.push({
        baseAsset,
        markets: assetMarkets,
        bestPrice,
        bestChange24h,
        avgFunding8h,
        totalVolume,
        totalOI,
        venueCount: assetMarkets.length,
        maxLeverage,
        primaryMarket,
      });
    });

    return rows.sort((a, b) => a.baseAsset.localeCompare(b.baseAsset));
  }, [markets, marketData]);

  // Filter rows by search query
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return aggregatedRows;

    const query = searchQuery.toLowerCase();
    return aggregatedRows.filter(row =>
      // Match on baseAsset name (e.g., "XYZ100", "AMD", "GOLD")
      row.baseAsset.toLowerCase().includes(query) ||
      // Match on full display name (e.g., "XYZ100-USDC")
      `${row.baseAsset}-USDC`.toLowerCase().includes(query)
    );
  }, [aggregatedRows, searchQuery]);

  // Format number helpers
  const formatPrice = (price: number) => {
    if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
    if (price >= 100) return price.toFixed(1);
    if (price >= 10) return price.toFixed(2);
    if (price >= 1) return price.toFixed(3);
    return price.toFixed(6);
  };

  const formatChange = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  const formatVolume = (volume: number) => {
    // Show full whole numbers with comma separators
    return volume.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatFunding = (funding: number) => {
    return `${(funding * 100).toFixed(4)}%`;
  };

  return (
    <div className="relative">
      {/* Trigger Button - Integrated style */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 hover:bg-[#1a1a1a] text-white rounded transition-all cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {selectedMarket ? (
            <>
              {/* Market Icon */}
              <AssetLogo
                symbol={selectedMarket.symbol}
                baseAsset={selectedMarket.baseAsset}
                size={32}
              />

              {/* Market Info */}
              <div className="flex flex-col items-start">
                <span className="font-semibold text-[14px]">{selectedMarket.baseAsset}-USDC</span>
                {(() => {
                  // Show venue count for this baseAsset
                  const venueCount = markets.filter(m => m.baseAsset === selectedMarket.baseAsset).length;
                  return (
                    <span className="text-[10px] text-[#6c7884] font-medium">
                      {venueCount > 1 ? `${venueCount} venues` : selectedMarket.provider}
                    </span>
                  );
                })()}
              </div>
            </>
          ) : (
            <span className="text-[#6c7884] text-[#6c7884] text-[13px]">Select Market</span>
          )}
        </div>
        <svg
          className={`w-3 h-3 text-[#6c7884] transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={3}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed bg-[#141414] border border-[#2d3748] rounded-lg shadow-[0_8px_32px_rgba(0,0,0,0.6)] z-20 overflow-hidden"
            style={{
              minWidth: '1000px',
              maxWidth: '1100px',
              top: buttonRef.current ? `${buttonRef.current.getBoundingClientRect().bottom + 8}px` : '0',
              left: buttonRef.current ? `${buttonRef.current.getBoundingClientRect().left}px` : '0'
            }}
          >
            {/* Search Bar */}
            <div className="p-2 border-b border-[#2d3748] bg-[#0a0a0a]">
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#6c7884]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    e.stopPropagation();
                    setSearchQuery(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Search"
                  className="w-full pl-8 pr-2 py-1.5 bg-[#1a1a1a] text-white text-[12px] rounded border border-[#2d3748] focus:outline-none focus:border-[#2962ff] focus:ring-1 focus:ring-[#2962ff]/30 transition-all"
                  autoFocus
                />
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-[#2d3748] bg-[#0a0a0a]">
              <button className="px-3 py-1 text-[11px] font-medium text-white bg-[#1a1a1a] rounded">
                All
              </button>
              <button className="px-3 py-1 text-[11px] font-medium text-[#6c7884] hover:text-white transition-colors">
                HIP-3
              </button>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-12 gap-3 px-4 py-1.5 text-[11px] font-medium text-[#6c7884] uppercase tracking-wide border-b border-[#2d3748] bg-[#0a0a0a] whitespace-nowrap">
              <div className="col-span-3">Symbol</div>
              <div className="col-span-2">Last Price</div>
              <div className="col-span-2">24h Change</div>
              <div className="col-span-1">8H Funding</div>
              <div className="col-span-2">Volume</div>
              <div className="col-span-2">Open Interest</div>
            </div>

            {/* Market List - Aggregated by base asset */}
            <div className="max-h-[450px] overflow-y-auto">
              {filteredRows.length > 0 ? (
                filteredRows.map((row) => {
                  const isSelected = selectedMarket?.baseAsset === row.baseAsset;

                  return (
                    <button
                      key={row.baseAsset}
                      onClick={() => {
                        // Select the primary market (native or first HIP-3)
                        onSelect(row.primaryMarket);
                        setIsOpen(false);
                        setSearchQuery('');
                      }}
                      className={`w-full grid grid-cols-12 gap-3 px-4 py-2 text-left hover:bg-[#1a1a1a] transition-colors border-l-2 cursor-pointer ${
                        isSelected
                          ? 'border-[#2962ff] bg-[#1a1a1a]'
                          : 'border-transparent'
                      }`}
                    >
                      {/* Symbol with leverage and venue count badges */}
                      <div className="col-span-3 flex items-center gap-1.5">
                        {/* Star icon placeholder */}
                        <svg className="w-3 h-3 text-[#6c7884]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                        <span className={`text-[12px] font-medium ${isSelected ? 'text-white' : 'text-[#e0e0e0]'}`}>
                          {row.baseAsset}-USDC
                        </span>
                        {/* Max leverage badge */}
                        <span className="px-1 py-0.5 bg-[#f0b90b]/10 text-[#f0b90b] text-[9px] font-semibold rounded">
                          {row.maxLeverage}x
                        </span>
                        {/* Venue count badge - only show if multiple venues */}
                        {row.venueCount > 1 && (
                          <span className="px-1 py-0.5 bg-[#2962ff]/20 text-[#2962ff] text-[9px] font-semibold rounded">
                            {row.venueCount} venues
                          </span>
                        )}
                      </div>

                      {/* Last Price (aggregated - using first available) */}
                      <div className="col-span-2">
                        <span className="text-[12px] text-white">
                          {row.bestPrice !== null ? formatPrice(row.bestPrice) : '-'}
                        </span>
                      </div>

                      {/* 24h Change (aggregated - using first available) */}
                      <div className="col-span-2">
                        <span className={`text-[12px] ${
                          row.bestChange24h !== null
                            ? row.bestChange24h >= 0
                              ? 'text-[#0ecb81]'
                              : 'text-[#f6465d]'
                            : 'text-[#6c7884]'
                        }`}>
                          {row.bestChange24h !== null && row.bestPrice !== null
                            ? (() => {
                                const absoluteChange = row.bestPrice * (row.bestChange24h / 100);
                                return `${absoluteChange >= 0 ? '+' : ''}${absoluteChange.toFixed(2)} / ${formatChange(row.bestChange24h)}`;
                              })()
                            : '-'}
                        </span>
                      </div>

                      {/* 8H Funding (averaged across venues) */}
                      <div className="col-span-1">
                        <span className={`text-[11px] ${
                          row.avgFunding8h !== null
                            ? row.avgFunding8h >= 0
                              ? 'text-[#0ecb81]'
                              : 'text-[#f6465d]'
                            : 'text-[#6c7884]'
                        }`}>
                          {row.avgFunding8h !== null ? formatFunding(row.avgFunding8h) : '-'}
                        </span>
                      </div>

                      {/* Volume (summed across all venues) */}
                      <div className="col-span-2">
                        <span className="text-[11px] text-[#b0b8c1]">
                          {row.totalVolume > 0 ? formatVolume(row.totalVolume) : '-'}
                        </span>
                      </div>

                      {/* Open Interest (summed across all venues) */}
                      <div className="col-span-2">
                        <span className="text-[11px] text-[#b0b8c1]">
                          {row.totalOI > 0 ? formatVolume(row.totalOI) : '-'}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-[#6c7884] text-[12px]">
                  No markets found matching "{searchQuery}"
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
